import type { CustomerStatus } from '@clockwise/clients/customers';
import { CircleDot } from 'lucide-react';
import type {
  FilterDefinition,
  FilterOption,
  FilterState,
} from '@/components/custom/filter-bar/filter-bar';

// ---------------------------------------------------------------------------
// Customer status filter
//
// The backend list endpoint accepts a single optional `status`
// (`active` | `inactive`). The UI adds a third user-facing choice, `all`,
// which simply omits the param. `active` is the default and is stripped
// from the URL to keep links clean.
// ---------------------------------------------------------------------------

export const CUSTOMER_STATUS_FILTERS = ['active', 'inactive', 'all'] as const;
export type CustomerStatusFilter = (typeof CUSTOMER_STATUS_FILTERS)[number];
export const DEFAULT_CUSTOMER_STATUS: CustomerStatusFilter = 'active';

const STATUS_OPTIONS: FilterOption[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
];

/**
 * FilterBar definitions for the customers list. Status is a first-class
 * single-select; `removable: false` makes it a sticky default (always
 * Active unless changed) rather than a chip whose × misleadingly snaps
 * back to the default.
 */
export const customerFilterDefinitions: FilterDefinition[] = [
  {
    key: 'status',
    label: 'Status',
    type: 'single-select',
    icon: CircleDot,
    options: STATUS_OPTIONS,
    removable: false,
    popoverWidthClass: 'w-40',
  },
];

// ---------------------------------------------------------------------------
// URL ↔ FilterState ↔ API params
// ---------------------------------------------------------------------------

function coerceStatus(value: string | undefined): CustomerStatusFilter {
  return (CUSTOMER_STATUS_FILTERS as readonly string[]).includes(value ?? '')
    ? (value as CustomerStatusFilter)
    : DEFAULT_CUSTOMER_STATUS;
}

/** Read the effective status choice from FilterState (defaults to active). */
function selectedStatus(state: FilterState): CustomerStatusFilter {
  const v = state.status;
  return v?.type === 'single-select' ? coerceStatus(v.value) : DEFAULT_CUSTOMER_STATUS;
}

/**
 * Build FilterState from the route search params. Status is always
 * present (defaulting to `active`) so its chip is always shown and acts
 * as a sticky default.
 */
export function urlToCustomerFilterState(search: { status?: string }): FilterState {
  return { status: { type: 'single-select', value: coerceStatus(search.status) } };
}

/** FilterState → URL search params. The default (`active`) is omitted. */
export function customerFilterStateToUrl(state: FilterState): {
  status: CustomerStatusFilter | undefined;
} {
  const status = selectedStatus(state);
  return { status: status === DEFAULT_CUSTOMER_STATUS ? undefined : status };
}

/** FilterState → list API params. `all` omits the status filter entirely. */
export function customerFilterStateToApiParams(state: FilterState): {
  status?: CustomerStatus;
} {
  const status = selectedStatus(state);
  return status === 'all' ? {} : { status };
}
